import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fanly_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

export async function login(email, password) {
  const { data } = await axios.post(`${API}/auth/login`, { email, password });
  setToken(data.access_token);
  return data.user;
}

export async function fetchMe() {
  const { data } = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
  return data;
}

export async function logout() {
  try {
    await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders() });
  } catch (e) {}
  clearToken();
}

export async function listProjects() {
  const { data } = await axios.get(`${API}/admin/projects`, { headers: authHeaders() });
  return data;
}

export async function getProject(slug) {
  const { data } = await axios.get(`${API}/admin/projects/${slug}`, { headers: authHeaders() });
  return data;
}

export async function patchProject(slug, body) {
  const { data } = await axios.patch(`${API}/admin/projects/${slug}`, body, { headers: authHeaders() });
  return data;
}

export async function removeSectionImage(slug, key) {
  const { data } = await axios.delete(`${API}/admin/projects/${slug}/section-image/${key}`, { headers: authHeaders() });
  return data;
}

export async function removeSectionVideo(slug, key) {
  const { data } = await axios.delete(`${API}/admin/projects/${slug}/section-video/${key}`, { headers: authHeaders() });
  return data;
}

export async function removeClosingImage(slug) {
  const { data } = await axios.delete(`${API}/admin/projects/${slug}/closing-image`, { headers: authHeaders() });
  return data;
}

export async function uploadAsset(slug, kind, file, extra = {}, onProgress) {
  const form = new FormData();
  form.append("file", file);
  Object.entries(extra).forEach(([k, v]) => {
    if (v !== undefined && v !== null) form.append(k, v);
  });
  const { data } = await axios.post(`${API}/admin/projects/${slug}/upload/${kind}`, form, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return data;
}

export async function listUsers() {
  const { data } = await axios.get(`${API}/admin/users`, { headers: authHeaders() });
  return data;
}

export async function createUser(body) {
  const { data } = await axios.post(`${API}/admin/users`, body, { headers: authHeaders() });
  return data;
}

export async function createProject(body) {
  const { data } = await axios.post(`${API}/admin/projects`, body, { headers: authHeaders() });
  return data;
}

export function readAudioDuration(file) {
  return new Promise((resolve) => {
    try {
      const a = new Audio();
      a.preload = "metadata";
      a.onloadedmetadata = () => resolve(Math.round(a.duration || 0));
      a.onerror = () => resolve(0);
      a.src = URL.createObjectURL(file);
    } catch (e) {
      resolve(0);
    }
  });
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function setTriggerDefault(slug, data) {
  const r = await axios.put(`${API}/admin/projects/${slug}/trigger/default`, data, { headers: authHeaders() });
  return r.data;
}

export async function setTriggerOverride(slug, content_piece, platform, data) {
  const r = await axios.put(`${API}/admin/projects/${slug}/trigger/override`,
    { content_piece, platform, data }, { headers: authHeaders() });
  return r.data;
}

export async function deleteTriggerOverride(slug, content_piece, platform) {
  const r = await axios.post(`${API}/admin/projects/${slug}/trigger/override/delete`,
    { content_piece, platform }, { headers: authHeaders() });
  return r.data;
}

export async function getTriggerStats(slug) {
  const r = await axios.get(`${API}/admin/projects/${slug}/trigger/stats`, { headers: authHeaders() });
  return r.data;
}

// ---------------- GROWTH: analytics ----------------
// Convert local calendar dates (YYYY-MM-DD) into half-open UTC ISO boundaries so the
// full last day is inclusive regardless of the admin's timezone:
//   start = local 00:00 of first day  ->  UTC ISO (inclusive)
//   end   = local 00:00 of the day AFTER the last day -> UTC ISO (exclusive)
function normalizeDateRange(params = {}) {
  const p = { ...params };
  if (p.date_from && /^\d{4}-\d{2}-\d{2}$/.test(p.date_from)) {
    p.date_from = new Date(`${p.date_from}T00:00:00`).toISOString();
  }
  if (p.date_to && /^\d{4}-\d{2}-\d{2}$/.test(p.date_to)) {
    const d = new Date(`${p.date_to}T00:00:00`);
    d.setDate(d.getDate() + 1);
    p.date_to = d.toISOString();
  }
  return p;
}

export async function getAnalytics(slug, params = {}) {
  const r = await axios.get(`${API}/admin/projects/${slug}/analytics`, { headers: authHeaders(), params: normalizeDateRange(params) });
  return r.data;
}
export async function getAnalyticsDimensions(slug) {
  const r = await axios.get(`${API}/admin/projects/${slug}/analytics/dimensions`, { headers: authHeaders() });
  return r.data;
}
export async function getPromoFragments(slug, params = {}) {
  const r = await axios.get(`${API}/admin/projects/${slug}/analytics/promo-fragments`, { headers: authHeaders(), params: normalizeDateRange(params) });
  return r.data;
}

// ---------------- GROWTH: fans (CRM) ----------------
export async function getFans(slug, params = {}) {
  const r = await axios.get(`${API}/admin/projects/${slug}/fans`, { headers: authHeaders(), params: normalizeDateRange(params) });
  return r.data;
}
export async function getFanDetail(slug, fanId) {
  const r = await axios.get(`${API}/admin/projects/${slug}/fans/${fanId}`, { headers: authHeaders() });
  return r.data;
}
export async function getOptIns(slug) {
  const r = await axios.get(`${API}/admin/projects/${slug}/opt-ins`, { headers: authHeaders() });
  return r.data;
}
export async function setPreviewWindow(slug, payload) {
  const r = await axios.post(`${API}/admin/projects/${slug}/preview-window`, payload, { headers: authHeaders() });
  return r.data;
}
export async function resetTestData(slug) {
  const r = await axios.post(`${API}/admin/projects/${slug}/test-data/reset`, {}, { headers: authHeaders() });
  return r.data;
}
// Pre-build the optimised preview clip so the first fan doesn't wait for encoding.
export function warmPreviewClip(slug) {
  return axios.get(`${API}/preview/stream-url/${slug}`).catch(() => {});
}
export async function getPreviewStreamUrl(slug) {
  // Admin "Preview this section" seeks arbitrary unsaved windows → needs the full-length master.
  const r = await axios.get(`${API}/admin/projects/${slug}/master/stream-url`, { headers: authHeaders() });
  return r.data.url;
}
export async function createSmartLink(slug, payload) {
  const r = await axios.post(`${API}/admin/projects/${slug}/smart-link`, payload, { headers: authHeaders() });
  return r.data;
}
export async function listSmartLinks(slug) {
  const r = await axios.get(`${API}/admin/projects/${slug}/smart-links`, { headers: authHeaders() });
  return r.data;
}

// ---------------- CUSTOM DOMAIN ----------------
export async function getDomain(slug) {
  const r = await axios.get(`${API}/admin/projects/${slug}/domain`, { headers: authHeaders() });
  return r.data;
}
export async function connectDomain(slug, domain) {
  const r = await axios.post(`${API}/admin/projects/${slug}/domain/connect`, { domain }, { headers: authHeaders() });
  return r.data;
}
export async function verifyDomain(slug) {
  const r = await axios.post(`${API}/admin/projects/${slug}/domain/verify`, {}, { headers: authHeaders() });
  return r.data;
}
export async function disconnectDomain(slug) {
  const r = await axios.post(`${API}/admin/projects/${slug}/domain/disconnect`, {}, { headers: authHeaders() });
  return r.data;
}

// ---------------- GROWTH: broadcast ----------------
export async function broadcastCount(slug, segment) {
  const r = await axios.post(`${API}/admin/projects/${slug}/broadcast/count`, segment, { headers: authHeaders() });
  return r.data;
}
export async function broadcastTest(slug, payload) {
  const r = await axios.post(`${API}/admin/projects/${slug}/broadcast/test`, payload, { headers: authHeaders() });
  return r.data;
}
export async function broadcastSend(slug, payload) {
  const r = await axios.post(`${API}/admin/projects/${slug}/broadcast/send`, payload, { headers: authHeaders() });
  return r.data;
}
export async function listBroadcasts(slug) {
  const r = await axios.get(`${API}/admin/projects/${slug}/broadcasts`, { headers: authHeaders() });
  return r.data;
}

// ---------------- GROWTH: QR ----------------
export async function translateSections(slug, regenerate_lang) {
  const r = await axios.post(`${API}/admin/projects/${slug}/translate`,
    regenerate_lang ? { regenerate_lang } : {}, { headers: authHeaders() });
  return r.data;
}

export async function fetchQrBlob(slug, params = {}, fmt = "png") {  const r = await axios.get(`${API}/admin/projects/${slug}/qr`, {
    headers: authHeaders(), params: { ...params, fmt }, responseType: "blob",
  });
  return r.data;
}
export async function exportFansCsv(slug, params = {}) {
  const r = await axios.get(`${API}/admin/projects/${slug}/fans.csv`, {
    headers: authHeaders(), params: normalizeDateRange(params), responseType: "blob",
  });
  return r.data;
}

export async function getMasterStreamUrl(slug) {
  const r = await axios.get(`${API}/admin/projects/${slug}/master/stream-url`, { headers: authHeaders() });
  return r.data;
}
export async function savePromoClip(slug, payload) {
  const r = await axios.put(`${API}/admin/projects/${slug}/promo-clip`, payload, { headers: authHeaders() });
  return r.data;
}
export async function deletePromoClip(slug, content_piece) {
  const r = await axios.post(`${API}/admin/projects/${slug}/promo-clip/delete`, { content_piece }, { headers: authHeaders() });
  return r.data;
}
export async function fetchPromoClipBlob(slug, params = {}) {
  const r = await axios.get(`${API}/admin/projects/${slug}/promo-clip.mp3`, {
    headers: authHeaders(), params, responseType: "blob",
  });
  return r.data;
}

export function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
}
